import { byRecencyThenSeverity, clockTime, relativeAge } from '../lib/alertTime';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import {
  Activity,
  ArrowUpRight,
  DollarSign,
  LayoutDashboard,
  Plus,
  ServerCog,
  ShieldCheck,
  TriangleAlert,
} from 'lucide-react';
import { Badge, Button, EmptyState, PageContainer, PageHeader, Section, Skeleton } from '../components/ui';
import { cardItem, staggerContainer } from '../lib/motion';
import { useRanges } from '../hooks/useRanges';
import { useTenantAlerts } from '../hooks/useAlerts';
import { useAuthStore } from '../store/useAuthStore';
import type { AlertSeverity } from '../components/monitoring/AlertFeed';
import type { RangeStatus } from '../api/types';

/**
 * Operator dashboard — clean professional "Operations overview" console.
 * Page header (where am I + primary action), a KPI readout grid, an active-ranges
 * panel, and a recent-activity feed — clean cards on a calm grid with a staggered
 * Framer Motion entrance. Real hooks: useRanges / useScenarios.
 */
export const Route = createFileRoute('/')({ component: Dashboard });

const STATUS_TONE: Record<string, { dot: string; label: string }> = {
  ready: { dot: 'bg-neon', label: 'Live' },
  attacking: { dot: 'bg-hud-amber', label: 'Under attack' },
  provisioning: { dot: 'bg-hud-cyan', label: 'Deploying' },
  pending: { dot: 'bg-hud-amber', label: 'Pending' },
  destroying: { dot: 'bg-hud-amber', label: 'Destroying' },
  failed: { dot: 'bg-hud-red', label: 'Failed' },
  completed: { dot: 'bg-txt-muted', label: 'Completed' },
};

/** Severity → row icon + tone for the recent-activity feed (real alerts only). */
const SEV_TONE: Record<AlertSeverity, { icon: typeof Activity; cls: string }> = {
  critical: { icon: TriangleAlert, cls: 'text-hud-red bg-hud-red/10' },
  warning: { icon: TriangleAlert, cls: 'text-hud-amber bg-hud-amber/10' },
  info: { icon: Activity, cls: 'text-hud-cyan bg-hud-cyan/10' },
};

export function Dashboard() {
  const navigate = useNavigate();
  const email = useAuthStore((s) => s.user?.email ?? 'operator');
  const name = email.split('@')[0]!.split('.')[0]!;
  const rangesQ = useRanges();
  const alertsQ = useTenantAlerts();

  const ranges = rangesQ.data ?? [];
  const liveCount = ranges.filter((r) => r.status === 'ready' || r.status === 'attacking').length;

  // Detections: real count from the live tenant alert feed. We only show a
  // number when the feed actually answered — pending/error stays honest ("—")
  // rather than rendering a fabricated total. No mock data (operator rule).
  const alerts = alertsQ.data ?? [];
  const detectionsValue = alertsQ.isSuccess ? String(alerts.length) : '—';

  // Severity split of the same live feed. Critical is what an instructor needs
  // to see without opening a range: a level-12+ Wazuh alert means a kill-chain
  // step landed, not that something logged.
  const criticalCount = alerts.filter((a) => a.severity === 'critical').length;
  const criticalValue = alertsQ.isSuccess ? String(criticalCount) : '—';
  const criticalHint = alertsQ.isSuccess
    ? `of ${alerts.length} live alert${alerts.length === 1 ? '' : 's'}`
    : alertsQ.isError
      ? 'feed unavailable'
      : 'connecting…';

  // Tenant spend right now. An instructor's second question after "is anything
  // wrong" is "what is this costing me", and the ranges table already knows —
  // the dashboard just never said it.
  const liveSpend = ranges
    .filter((r) => !['destroyed', 'completed', 'failed'].includes(r.status))
    .reduce((sum, r) => sum + (r.costSoFar ?? 0), 0);

  // FOUR tiles carrying TWO facts was the problem: "Detections 8" and
  // "Critical alerts 2 of 8" are the same eight alerts counted twice, and a
  // count of deployable scenarios is catalog trivia, not an operational signal
  // — it belongs on the catalog, which already shows it.
  //
  // What an instructor actually opens this page to learn, in order:
  //   1. is anything demanding attention RIGHT NOW  (critical alerts)
  //   2. what is running                            (ranges + how many live)
  //   3. what is it costing                         (spend)
  // So: three tiles, each a distinct question, severity first.
  const kpis = [
    {
      label: 'Needs attention',
      value: criticalValue,
      icon: ShieldCheck,
      hint: criticalCount > 0 ? criticalHint : 'no critical alerts',
      tone: criticalCount > 0 ? 'text-hud-red' : 'text-txt-muted',
      trend: false,
      emphasis: criticalCount > 0,
    },
    {
      label: 'Active ranges',
      value: String(liveCount),
      icon: ServerCog,
      // Was "4 total", which counts destroyed ranges — a number that only ever
      // grows and tells an operator nothing.
      hint: `${detectionsValue} detection${detectionsValue === '1' ? '' : 's'} across them`,
      tone: 'text-neon',
      trend: false,
      emphasis: false,
    },
    {
      label: 'Spend',
      value: rangesQ.isSuccess ? `$${liveSpend.toFixed(2)}` : '—',
      icon: DollarSign,
      hint: liveCount > 0 ? 'on ranges running now' : 'nothing running',
      tone: 'text-txt-2',
      trend: false,
      emphasis: false,
    },
  ];

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={LayoutDashboard}
        title={`Welcome back, ${name}`}
        description="A live overview of your ranges, detections, and training operations."
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Overview' }]}
        actions={
          <Button leftIcon={<Plus className="size-4" />} onClick={() => navigate({ to: '/scenarios' })}>
            Deploy range
          </Button>
        }
      />

      <motion.div
        variants={staggerContainer}
        initial="hidden"
        animate="show"
        className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3"
      >
        {kpis.map((k) => (
          <motion.div key={k.label} variants={cardItem} whileHover={{ y: -2 }} className="hud-panel p-4">
            <div className="flex items-start justify-between">
              <span className="eyebrow">{k.label}</span>
              <span className={`flex size-8 items-center justify-center rounded-lg bg-neon-glow ${k.tone}`}>
                <k.icon className="size-4" />
              </span>
            </div>
            <div className="readout mt-3">
              <span className="readout__value text-[30px]">{k.value}</span>
              <span className="mt-1 flex items-center gap-1 text-[12px] text-txt-2">
                {k.trend ? <ArrowUpRight className="size-3.5 text-neon" /> : null}
                {k.hint}
              </span>
            </div>
          </motion.div>
        ))}
      </motion.div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <Section
            title="Active ranges"
            description="Ranges currently deployed in your tenant."
            action={
              <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/ranges' })}>
                View all
              </Button>
            }
          >
            {rangesQ.isPending ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} height={64} radius="md" />
                ))}
              </div>
            ) : ranges.length === 0 ? (
              <div className="hud-panel p-2">
                <EmptyState
                  illustration={<ServerCog className="size-9 text-txt-muted" />}
                  title="No active ranges"
                  description="Deploy a scenario to spin up your first training range."
                  cta={{ label: 'Browse scenarios', onClick: () => navigate({ to: '/scenarios' }) }}
                />
              </div>
            ) : (
              <motion.div variants={staggerContainer} initial="hidden" animate="show" className="space-y-2">
                {ranges.slice(0, 6).map((r) => {
                  const tone = STATUS_TONE[r.status as RangeStatus] ?? STATUS_TONE.completed!;
                  return (
                    <motion.div key={r.id} variants={cardItem}>
                      <Link
                        to="/ranges/$id"
                        params={{ id: r.id }}
                        className="hud-panel group flex items-center gap-4 p-3.5 transition-transform hover:-translate-y-0.5 cursor-pointer"
                      >
                        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-panel text-txt-2">
                          <ServerCog className="size-5" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-[14px] font-semibold text-txt-bright">
                            {r.scenarioName || r.scenarioSlug}
                          </div>
                          <div className="mt-0.5 flex items-center gap-2 text-[12px] text-txt-muted">
                            <span className={`size-1.5 rounded-full ${tone.dot}`} />
                            {tone.label}
                            <span className="text-txt-muted/50">·</span>
                            <span className="tabular">{r.vmCount} VMs</span>
                          </div>
                        </div>
                        <ArrowUpRight className="size-4 text-txt-muted opacity-0 transition-opacity group-hover:opacity-100" />
                      </Link>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </Section>
        </div>

        <div>
          <Section title="Recent activity" description="Detections from your ranges' live alert feed.">
            {alertsQ.isPending ? (
              <div className="hud-panel space-y-2 p-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} height={48} radius="md" />
                ))}
              </div>
            ) : alerts.length > 0 ? (
              <div className="hud-panel divide-y divide-bd p-1">
                {byRecencyThenSeverity(alerts).slice(0, 6).map((a) => {
                  const sev = SEV_TONE[a.severity] ?? SEV_TONE.info!;
                  const Icon = sev.icon;
                  return (
                    <div key={a.id} className="flex items-start gap-3 px-3 py-2.5">
                      <span className={`mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg ${sev.cls}`}>
                        <Icon className="size-3.5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[13px] leading-snug text-txt">{a.title}</div>
                        <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-txt-muted">
                          <span title={a.ts}>{clockTime(a.ts)}</span>
                          <span className="text-txt-muted/50">·</span>
                          <span>{relativeAge(a.ts)}</span>
                          {a.rangeId ? (
                            <>
                              <span className="text-txt-muted/50">·</span>
                              <span className="tabular">{a.rangeId}</span>
                            </>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState
                illustration={<Activity className="size-9 text-txt-muted" />}
                title="No recent activity yet"
                description="Detections from your ranges appear here as attacks run and the SIEM pipeline flags them."
              />
            )}
            {alertsQ.isError ? (
              <div className="mt-3">
                <Badge variant="warn">Alert feed unavailable</Badge>
              </div>
            ) : alertsQ.isSuccess ? (
              <div className="mt-3">
                <Badge variant="info">Live · auto-refreshing</Badge>
              </div>
            ) : null}
          </Section>
        </div>
      </div>
    </PageContainer>
  );
}
